import { useGameStore } from '@/store/gameStore'
import { LOAN_OPTIONS, Loan } from '@/config/sponsorshipConfig'
import { SectionDeck, IconTile, StatStrip, EmptyStateRich } from '../components/CourtVision'
import { Banknote, Coins, CreditCard, Landmark, Rocket, Lock, Scale, HandCoins, type LucideIcon } from 'lucide-react'

const LOAN_ICONS: Record<string, LucideIcon> = {
  micro_loan: Coins,
  small_loan: Banknote,
  medium_loan: CreditCard,
  large_loan: Landmark,
  expansion_loan: Rocket,
}

export function LoansTabContent() {
  const { reputation, courts, money, addMoney, subtractMoney, activeLoans, addActiveLoan, removeActiveLoan } = useGameStore()

  const canTakeLoan = (loan: Loan): { can: boolean; reason?: string } => {
    if (activeLoans.length >= 2) return { can: false, reason: 'Máximo 2 préstamos' }
    if (activeLoans.some(a => a.loanId === loan.id)) return { can: false, reason: 'Ya activo' }
    for (const req of loan.requirements) {
      if (req.type === 'reputation' && reputation < req.value)
        return { can: false, reason: `Rep. mínima ${req.value}` }
      if (req.type === 'courts' && courts.length < req.value)
        return { can: false, reason: `Mínimo ${req.value} pistas` }
    }
    return { can: true }
  }

  const takeLoan = (loan: Loan) => {
    addActiveLoan({ loanId: loan.id, remainingMonths: loan.duration, totalPaid: 0 })
    addMoney(loan.amount, `Prestamo: ${loan.name}`)
  }

  const payOffLoan = (loanId: string) => {
    const active = activeLoans.find(a => a.loanId === loanId)
    if (!active) return
    const loan = LOAN_OPTIONS.find(l => l.id === loanId)
    if (!loan) return
    const remaining = active.remainingMonths * loan.monthlyPayment
    if (money >= remaining) {
      subtractMoney(remaining, `Liquidacion: ${loan.name}`)
      removeActiveLoan(loanId)
    }
  }

  const totalDebt = activeLoans.reduce((sum, a) => {
    const loan = LOAN_OPTIONS.find(l => l.id === a.loanId)
    return sum + (loan ? a.remainingMonths * loan.monthlyPayment : 0)
  }, 0)

  return (
    <div>
      {totalDebt > 0 && (
        <div style={{ marginBottom: '1.25rem' }}>
          <StatStrip
            label="Deuda activa"
            value={<span className="money-negative">{totalDebt.toLocaleString('es-ES')}€</span>}
            suffix={<IconTile icon={Scale} tone="warning" />}
          />
        </div>
      )}

      <SectionDeck title="Préstamos activos" icon={Landmark} meta={`${activeLoans.length}/2`}>
        {activeLoans.length === 0 ? (
          <EmptyStateRich
            icon={Landmark}
            title="El club no debe ni un euro"
            sub="Si necesitas liquidez para crecer, abajo tienes la ventanilla del banco"
          />
        ) : (
          <div className="space-y-2">
            {activeLoans.map(active => {
              const loan = LOAN_OPTIONS.find(l => l.id === active.loanId)
              if (!loan) return null
              const remaining = active.remainingMonths * loan.monthlyPayment
              const progress = ((loan.duration - active.remainingMonths) / loan.duration) * 100
              const LoanIcon = LOAN_ICONS[loan.id] ?? Banknote
              return (
                <div key={active.loanId} className="row-tile" style={{ flexDirection: 'column', alignItems: 'stretch', gap: '0.55rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem' }}>
                    <IconTile icon={LoanIcon} tone="warning" />
                    <div className="row-tile-main">
                      <div className="row-tile-title">{loan.name}</div>
                      <div className="row-tile-sub">{active.remainingMonths} meses restantes · {loan.monthlyPayment.toLocaleString('es-ES')}€/mes</div>
                    </div>
                    <div className="row-tile-side">
                      <span className="money-negative" style={{ fontWeight: 700 }}>
                        {remaining.toLocaleString('es-ES')}€
                      </span>
                      {money >= remaining && (
                        <button
                          onClick={() => payOffLoan(active.loanId)}
                          className="btn btn-success"
                          style={{ padding: '0.2rem 0.5rem', fontSize: '0.72rem' }}
                        >Liquidar</button>
                      )}
                    </div>
                  </div>
                  <div className="progress-track">
                    <div className="progress-fill angled success" style={{ width: `${progress}%` }} />
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </SectionDeck>

      <SectionDeck title="Solicitar" icon={HandCoins}>
        <div className="space-y-2">
          {LOAN_OPTIONS.map(loan => {
            const { can, reason } = canTakeLoan(loan)
            const LoanIcon = LOAN_ICONS[loan.id] ?? Banknote

            if (!can) {
              return (
                <div key={loan.id} className="row-tile" style={{ opacity: 0.6 }}>
                  <IconTile icon={LoanIcon} tone="muted" />
                  <div className="row-tile-main">
                    <div className="row-tile-title">{loan.name}</div>
                    <div className="row-tile-sub">
                      {loan.amount.toLocaleString('es-ES')}€ · {loan.interestRate}% · {loan.duration} meses
                    </div>
                  </div>
                  <span className="pill pill-warning"><Lock size={10} /> {reason}</span>
                </div>
              )
            }

            return (
              <button key={loan.id} className="row-tile" onClick={() => takeLoan(loan)}>
                <IconTile icon={LoanIcon} tone="cta" />
                <div className="row-tile-main">
                  <div className="row-tile-title">{loan.name}</div>
                  <div className="row-tile-sub">{loan.interestRate}% de interés · {loan.duration} meses</div>
                </div>
                <div className="row-tile-side">
                  <span className="money-positive" style={{ fontWeight: 700 }}>
                    +{loan.amount.toLocaleString('es-ES')}€
                  </span>
                  <span className="money-negative" style={{ fontSize: '0.72rem' }}>
                    -{loan.monthlyPayment.toLocaleString('es-ES')}€/mes
                  </span>
                </div>
              </button>
            )
          })}
        </div>
      </SectionDeck>
    </div>
  )
}
